import React, { useState } from 'react';

import { Card } from 'antd';
import { cardShadowStyle } from './SharedCard.style';

interface IProps {
  title?: React.ReactNode;
  extra?: React.ReactNode;
  style?: React.CSSProperties;
  customClasses?: string;
  size?: 'default' | 'small' | 'middle';
  hideExtra?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /**
   * Stretch the body to the card's height and spread its blocks, so a card shorter than its
   * neighbours in a row does not end with a slab of empty space under it.
   */
  fillHeight?: boolean;
  children?: React.ReactNode;
}

const collapsibleTitleStyle: React.CSSProperties = {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0',
  borderRadius: 4,
  transition: 'background-color 0.2s ease',
  width: '100%',
};

export const SharedCard: React.FC<IProps> = ({
  title,
  extra,
  style,
  children,
  customClasses,
  size = 'default',
  hideExtra = false,
  collapsible = false,
  defaultExpanded = true,
  fillHeight = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const cardTitle = collapsible ? (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setExpanded((prev) => !prev)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((prev) => !prev);
        }
      }}
      style={collapsibleTitleStyle}
    >
      <div style={{ flex: 1, minWidth: 0 }}>{title}</div>
    </div>
  ) : (
    title
  );

  const collapsedBodyStyle = collapsible && !expanded ? { padding: 0, display: 'none' } : {};
  const fillStyles: React.CSSProperties = fillHeight
    ? { flex: 1, display: 'flex', flexDirection: 'column' }
    : {};

  return (
    <Card
      title={cardTitle}
      extra={hideExtra ? undefined : extra}
      size={size}
      style={{
        ...cardShadowStyle,
        ...(fillHeight && { height: '100%', display: 'flex', flexDirection: 'column' }),
        ...style,
      }}
      className={customClasses}
      styles={{ body: { ...collapsedBodyStyle, ...fillStyles } }}
    >
      <div
        style={{
          overflow: 'hidden',
          maxHeight: collapsible && !expanded ? 0 : 5000,
          opacity: collapsible && !expanded ? 0 : 1,
          transition: 'max-height 0.3s ease, opacity 0.2s ease',
          ...fillStyles,
          // The blocks keep their own size; the leftover height goes between them rather than
          // piling up under the last one.
          ...(fillHeight && { justifyContent: 'space-between' }),
        }}
      >
        {children}
      </div>
    </Card>
  );
};
